import { useTranslation } from "react-i18next";
import { displayToast } from "../../components/app/AppToast";
import { apiClient, useApiQuery, useApiRevalidate } from "../../services/api";
import { useSingleFlightAction } from "../../hooks/useSingleFlightAction";
import { useActiveLang } from "../app/hooks/useActiveLang";
import { gaEventPartnerObjectionAnswered } from "../../tools/analytics/domains/partners.analytics";

/**
 * "Partner sharing" (Figma O4, docs/partner-objection.md REQ-09), for the
 * settings sheet of a phone and the settings modal of a desktop: the choice of
 * the user, the partners of Juisci, and the one way to change a switch.
 *
 * One save at a time. Every switch waits for the save and the refetch, so a
 * failure leaves it in its previous state.
 */
export function usePartnerSharing(): {
  sharingQuery: Exclude<
    ReturnType<typeof useApiQuery<"/webapp/private/users/me/partner-sharing">>,
    { state: "idle" }
  >;
  partnersQuery: Exclude<
    ReturnType<typeof useApiQuery<"/webapp/private/partners">>,
    { state: "idle" }
  >;
  isSaving: boolean;
  changeSharing: (organisationId: string | null, sharing: boolean) => void;
} {
  const { t } = useTranslation();
  const { activeLang } = useActiveLang();
  const sharingQuery = useApiQuery("/webapp/private/users/me/partner-sharing", {
    query: { language: activeLang },
  });
  const partnersQuery = useApiQuery("/webapp/private/partners", {});
  const revalidate = useApiRevalidate();
  const saveAction = useSingleFlightAction();

  /** `organisationId` null is the global switch, for all partners. */
  function changeSharing(organisationId: string | null, sharing: boolean) {
    void saveAction.run(async () => {
      try {
        await apiClient.put("/webapp/private/users/me/partner-sharing", {
          json: { organisationId, sharing },
        });
      } catch {
        displayToast(t("Something went wrong. Please try again."), "error");
        return;
      }
      gaEventPartnerObjectionAnswered({
        surface: "settings",
        objected: !sharing,
        organisationId,
      });
      // The contact line of every room page (REQ-07) reads the choice
      // through `partnerContact`.
      await Promise.all([
        sharingQuery.refetch(),
        revalidate("/webapp/private/rooms/:id", { param: {} }),
      ]);
    });
  }

  return {
    sharingQuery,
    partnersQuery,
    isSaving: saveAction.state.status === "running",
    changeSharing,
  };
}
